/**
 * Een ingelezen Excel omzetten naar onderdelen voor de import.
 *
 * Twee bronnen, één omzetting:
 *  - de **TopSolid-export**: blad `Import`, met de Engelse kolomnamen (`number`,
 *    `count`, `description`, `material`, `face_0`, …) en op de derde regel dezelfde
 *    kolommen in het Nederlands;
 *  - een bestaande **houtstaat-werkmap**: blad `Houtstaat `, met de Nederlandse
 *    kolomnamen.
 *
 * De kolomherkenning gaat op naam, niet op positie: in de werkmappen die er nu zijn
 * staan de kolommen in verschillende volgorde en soms met een andere schrijfwijze. Wat
 * niet herkend wordt, wordt gemeld — met de kolomnamen die er wél stonden, zodat de
 * voorbereider ziet wat er mis is in plaats van een lege import te krijgen.
 *
 * De hiërarchie komt maar deels uit het bestand: ruimte en bouwelement staan erin,
 * deck en sectie niet. Die vraagt het importscherm; zonder die twee is niet te zeggen
 * waar het werk in het schip zit.
 */
import type { Onderdeel } from '@houtstaat/engine';

import type { Blad } from './xlsx';

/** Per veld de kolomnamen die we herkennen, in het Engels en het Nederlands. */
const KOLOMMEN = {
  nummer: ['number', 'nummer', 'nr', 'nr.'],
  aantal: ['count', 'aantal', 'aant', 'aant.'],
  benaming: ['description', 'omschrijving', 'benaming'],
  lengte: ['length', 'lengte', 'net length', 'net lengte', 'l'],
  breedte: ['width', 'breedte', 'net width', 'net breedte', 'b'],
  dikte: ['thickness', 'dikte', 'net thickness', 'net dikte', 'd'],
  materiaal: ['material', 'materiaalcode', 'materiaal'],
  zijde0: ['face_0', 'zijde_0', 'zijde 0', 'zijde0'],
  zijde5: ['face_5', 'zijde_5', 'zijde 5', 'zijde5'],
  zijde1: ['length edge 1_1', 'lengtezijde1_1', '1_1'],
  zijde2: ['width edge 1_2', 'breedtezijde1_2', '1_2'],
  zijde3: ['length edge 2_3', 'lengtezijde2_3', '2_3'],
  zijde4: ['width edge 2_4', 'breedtezijde2_4', '2_4'],
  opmerking: ['remarks', 'opmerking', 'opmerkingen'],
  dekkend: ['covering', 'dekkend'],
  nesting: ['nesting'],
  laser: ['laser'],
  buigen: ['bend part', 'buigen'],
  ruimte: ['ruimte', 'room'],
  // Niet `houtstaat`: die kolom staat in de TopSolid-export vóór de omschrijving en
  // bevat het karnummer (`0716-51-6110-17`), niet de naam van het bouwelement.
  bouwelement: ['omschrijving bouwelement', 'bouwelement'],
} as const;

type Veld = keyof typeof KOLOMMEN;

/** De velden zonder welke een regel geen onderdeel is. */
const VERPLICHT: Veld[] = ['aantal', 'benaming', 'lengte', 'breedte', 'dikte', 'materiaal'];

const normaliseer = (tekst: string): string => tekst.trim().toLowerCase().replace(/\s+/g, ' ');

export interface Doelplaatsing {
  bouwnummer: string;
  /** Naam van het deck; het deck zit niet in het nummer. */
  deck: string;
  sectie: string;
  /** Gebruikt als het bestand geen ruimte bevat. */
  ruimte: string;
  ruimteNaam: string;
  kar: string;
  karNaam: string;
  /** Gebruikt als het bestand geen bouwelement bevat. */
  bouwelement: string;
  bouwelementNaam: string;
}

export interface Importresultaat {
  onderdelen: Onderdeel[];
  blad: string;
  /** Kolommen die in het bestand stonden, voor de melding bij een mislukte herkenning. */
  gevondenKolommen: string[];
  ontbrekend: Veld[];
  /** Regels die zijn overgeslagen, met de reden. */
  overgeslagen: Array<{ rij: number; reden: string }>;
}

/**
 * Zoekt de kopregel: de eerste rij waarin minstens vier bekende kolomnamen staan.
 *
 * In het blad `Import` staat de Engelse kop op rij 1 en de Nederlandse op rij 3, met
 * technische rijen ertussen; in een houtstaat-werkmap staat de kop verder naar beneden.
 * Zoeken is daarom betrouwbaarder dan een vast rijnummer.
 */
function vindKop(rijen: string[][]): { rij: number; kolommen: Map<Veld, number> } | null {
  const alle = new Map<string, Veld>();
  for (const [veld, namen] of Object.entries(KOLOMMEN) as Array<[Veld, readonly string[]]>) {
    for (const naam of namen) if (!alle.has(naam)) alle.set(naam, veld);
  }

  let beste: { rij: number; kolommen: Map<Veld, number> } | null = null;
  for (let i = 0; i < Math.min(rijen.length, 40); i++) {
    const kolommen = new Map<Veld, number>();
    (rijen[i] ?? []).forEach((cel, index) => {
      const veld = alle.get(normaliseer(cel));
      // Eerste voorkomen wint: `net length` staat verderop nog eens en zou de
      // eigenlijke lengtekolom overschrijven.
      if (veld && !kolommen.has(veld)) kolommen.set(veld, index);
    });
    if (kolommen.size >= 4 && (!beste || kolommen.size > beste.kolommen.size)) {
      beste = { rij: i, kolommen };
    }
  }
  return beste;
}

const getal = (tekst: string | undefined): number => {
  const n = Number(String(tekst ?? '').replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
};

const vlag = (tekst: string | undefined): boolean =>
  ['y', 'j', 'ja', 'yes', 'x', '1', 'true', 'waar'].includes(normaliseer(tekst ?? ''));

/**
 * Zet één blad om naar onderdelen.
 *
 * `doel` bepaalt waar het werk landt. Staat er in het bestand een ruimte of een
 * bouwelement, dan gaat die vóór wat in `doel` is ingevuld: het bestand weet dat
 * beter. Deck, sectie en kar komen altijd uit `doel`.
 */
export function naarOnderdelen(blad: Blad, doel: Doelplaatsing): Importresultaat {
  const kop = vindKop(blad.rijen);
  const gevondenKolommen = kop
    ? (blad.rijen[kop.rij] ?? []).filter(Boolean)
    : (blad.rijen.find((r) => r.filter(Boolean).length > 3) ?? []).filter(Boolean);

  if (!kop) {
    return {
      onderdelen: [],
      blad: blad.naam,
      gevondenKolommen,
      ontbrekend: VERPLICHT,
      overgeslagen: [],
    };
  }

  const ontbrekend = VERPLICHT.filter((veld) => !kop.kolommen.has(veld));
  if (ontbrekend.length > 0) {
    return { onderdelen: [], blad: blad.naam, gevondenKolommen, ontbrekend, overgeslagen: [] };
  }

  const cel = (rij: string[], veld: Veld): string | undefined => {
    const index = kop.kolommen.get(veld);
    return index == null ? undefined : rij[index];
  };

  const onderdelen: Onderdeel[] = [];
  const overgeslagen: Array<{ rij: number; reden: string }> = [];
  /** Per bouwelement een eigen nummering, zodat nr 1 op elke kar weer bij 1 begint. */
  const tellers = new Map<string, number>();

  for (let i = kop.rij + 1; i < blad.rijen.length; i++) {
    const rij = blad.rijen[i];
    if (!rij || rij.every((c) => c === '')) continue;

    const benaming = (cel(rij, 'benaming') ?? '').trim();
    const materiaal = (cel(rij, 'materiaal') ?? '').trim();
    const lengte = getal(cel(rij, 'lengte'));
    const breedte = getal(cel(rij, 'breedte'));
    const dikte = getal(cel(rij, 'dikte'));
    const aantal = getal(cel(rij, 'aantal'));

    // De technische rijen in het blad `Import` slaan we stil over: de rij met
    // `TOP_col0`, `TOP_col1`, … en de rij met de kolomindexen `0`, `1`, `2`, …. Die
    // laatste komt anders als onderdeel binnen, want 3 × 4 × 5 is een geldige maat.
    // Een materiaalcode is nooit een kaal getal en een benaming nooit één cijfer.
    if (!benaming && !materiaal) continue;
    if (/^\d+$/.test(benaming) && /^\d+$/.test(materiaal)) continue;
    if (/^TOP_col\d+$/i.test(benaming) || /^TOP_col\d+$/i.test(materiaal)) continue;
    if (lengte <= 0 || breedte <= 0 || dikte <= 0) {
      overgeslagen.push({ rij: i + 1, reden: `${benaming || materiaal}: geen geldige maatvoering` });
      continue;
    }
    if (aantal <= 0) {
      overgeslagen.push({ rij: i + 1, reden: `${benaming || materiaal}: aantal is 0` });
      continue;
    }

    const ruimteUitBestand = (cel(rij, 'ruimte') ?? '').trim();
    const elementUitBestand = (cel(rij, 'bouwelement') ?? '').trim();
    const plaatsing = {
      bouwnummer: doel.bouwnummer,
      deck: doel.deck,
      sectie: doel.sectie,
      ruimte: doel.ruimte,
      ruimteNaam: ruimteUitBestand || doel.ruimteNaam,
      kar: doel.kar,
      karNaam: doel.karNaam,
      bouwelement: doel.bouwelement,
      bouwelementNaam: elementUitBestand || doel.bouwelementNaam,
    };

    const sleutel = `${plaatsing.ruimte}|${plaatsing.kar}|${plaatsing.bouwelement}`;
    const teller = (tellers.get(sleutel) ?? 0) + 1;
    tellers.set(sleutel, teller);
    // Het nummer uit het bestand blijft staan zoals het er staat — dat is wat de
    // tekenkamer heeft gebruikt. Het id loopt op de eigen teller, want een werkmap kan
    // hetzelfde nummer meer dan eens bevatten en dan zouden twee onderdelen op
    // hetzelfde id landen.
    const nummer = (cel(rij, 'nummer') ?? '').trim() || String(teller);

    onderdelen.push({
      // Het id volgt de boom: boot, sectie, ruimte, kar, bouwelement, teller.
      id: [
        plaatsing.bouwnummer,
        plaatsing.sectie,
        plaatsing.ruimte,
        plaatsing.kar,
        plaatsing.bouwelement,
        String(teller),
      ].join('-'),
      nummer,
      aantal,
      benaming: benaming || materiaal,
      lengte,
      breedte,
      dikte,
      materiaal,
      zijden: {
        zijde0: (cel(rij, 'zijde0') ?? '').trim(),
        zijde5: (cel(rij, 'zijde5') ?? '').trim(),
        zijde1: (cel(rij, 'zijde1') ?? '').trim(),
        zijde2: (cel(rij, 'zijde2') ?? '').trim(),
        zijde3: (cel(rij, 'zijde3') ?? '').trim(),
        zijde4: (cel(rij, 'zijde4') ?? '').trim(),
      },
      plaatsing,
      ...(vlag(cel(rij, 'dekkend')) ? { dekkend: true } : {}),
      ...(vlag(cel(rij, 'nesting')) ? { nesting: true } : {}),
      ...(vlag(cel(rij, 'laser')) ? { laser: true } : {}),
      ...(vlag(cel(rij, 'buigen')) ? { buigen: true } : {}),
      ...((cel(rij, 'opmerking') ?? '').trim() ? { opmerking: (cel(rij, 'opmerking') ?? '').trim() } : {}),
    });
  }

  return { onderdelen, blad: blad.naam, gevondenKolommen, ontbrekend: [], overgeslagen };
}

/** Het blad dat het meest waarschijnlijk de onderdelen bevat. */
export function kiesBlad(bladen: Blad[], voorkeur: string[]): Blad | null {
  for (const naam of voorkeur) {
    const gevonden = bladen.find((b) => normaliseer(b.naam) === normaliseer(naam));
    if (gevonden) return gevonden;
  }
  // Anders het blad waar de meeste bekende kolommen in staan.
  let beste: { blad: Blad; score: number } | null = null;
  for (const blad of bladen) {
    const kop = vindKop(blad.rijen);
    const score = kop?.kolommen.size ?? 0;
    if (score > 0 && (!beste || score > beste.score)) beste = { blad, score };
  }
  return beste?.blad ?? null;
}

export const VELDLABEL: Record<Veld, string> = {
  nummer: 'nummer',
  aantal: 'aantal',
  benaming: 'benaming',
  lengte: 'lengte',
  breedte: 'breedte',
  dikte: 'dikte',
  materiaal: 'materiaalcode',
  zijde0: 'afwerking zijde 0',
  zijde5: 'afwerking zijde 5',
  zijde1: 'kant 1_1',
  zijde2: 'kant 1_2',
  zijde3: 'kant 2_3',
  zijde4: 'kant 2_4',
  opmerking: 'opmerking',
  dekkend: 'dekkend',
  nesting: 'nesting',
  laser: 'laser',
  buigen: 'buigen',
  ruimte: 'ruimte',
  bouwelement: 'bouwelement',
};
